import { LoaderCircle } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { AppLayout } from '@/_shared/components/AppLayout';
import { WorkspaceRoute } from '@/_shared/components/WorkspaceRoute';
import { useAuth } from '@/_shared/hooks/useAuth';

import { AcceptInvite } from './auth/views/AcceptInvite';
import { DevAuth } from './auth/views/DevAuth';
import { Login } from './auth/views/Login';
import { NewWorkspace } from './auth/views/NewWorkspace';
import { Chat } from './chat/views/Chat';
import { ChatList } from './chat/views/ChatList';
import { Dashboard } from './dashboard/views/Dashboard';
import { DashboardList } from './dashboard/views/DashboardList';
import { PublicDashboard } from './dashboard/views/PublicDashboard';
import { MeasurementPlanDetail } from './measurement-plan/views/MeasurementPlanDetail';
import { MeasurementPlanList } from './measurement-plan/views/MeasurementPlanList';
import { SettingsShell } from './settings/components/SettingsShell';
import { McpClients } from './settings/views/McpClients';
import { Settings } from './settings/views/Settings';
import { SettingsIntegrations } from './settings/views/SettingsIntegrations';
import { SettingsMembers } from './settings/views/SettingsMembers';
import { SettingsScm } from './settings/views/SettingsScm';
import { SettingsSlack } from './settings/views/SettingsSlack';
import { SettingsSources } from './settings/views/SettingsSources';
import { SettingsWarehouse } from './settings/views/SettingsWarehouse';
import { WorkflowDetail } from './workflow/views/WorkflowDetail';
import { Workflows } from './workflow/views/Workflows';

const DocDetail = lazy(() => import('./context-hub/views/DocDetail.js'));
const ContextHub = lazy(() => import('./context-hub/views/ContextHub.js'));
const ContextItemDetail = lazy(() =>
  import('./context-hub/views/ContextItemDetail.js').then((m) => ({ default: m.ContextItemDetail })),
);

function ProtectedRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, workspaces, loading, error, logout } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    const isLocal =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="max-w-sm text-center">
          <p className="text-sm font-medium text-foreground">Unable to verify your session</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Your session may have expired or the server is temporarily unavailable.
          </p>
          <div className="mt-5 flex flex-col items-center gap-2">
            <button
              type="button"
              className="text-sm text-primary underline underline-offset-4 hover:text-primary/80"
              onClick={() => window.location.reload()}
            >
              Refresh page
            </button>
            <button
              type="button"
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
              onClick={async () => {
                await logout();
                window.location.href = '/login';
              }}
            >
              Sign out and return to login
            </button>
            {isLocal && (
              <p className="mt-2 text-xs text-muted-foreground">
                Dev mode: check your terminal for a fresh magic link.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // User exists but has no workspaces — check for pending invite first
  if (workspaces.length === 0) {
    const pendingInvite = sessionStorage.getItem('pending_invite');
    if (pendingInvite) {
      return <Navigate to={`/invite/${pendingInvite}`} replace />;
    }
    return <Navigate to="/new-workspace" replace />;
  }

  // Workspace membership validation is handled by WorkspaceRoute
  return <>{children}</>;
}

function LoginRoute(): React.ReactElement {
  const { user, workspace, workspaces, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (user && workspace) return <Navigate to={`/w/${workspace.id}`} replace />;
  if (user && workspaces.length > 0) return <Navigate to={`/w/${workspaces[0]!.id}`} replace />;
  if (user) return <Navigate to="/new-workspace" replace />;
  return <Login />;
}

/** Redirect legacy bookmarks to workspace-prefixed URLs, preserving the full path. */
function LegacyRedirect(): React.ReactElement {
  const { user, workspace, workspaces, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Check for pending invite before onboarding redirect
  const pendingInvite = sessionStorage.getItem('pending_invite');
  if (pendingInvite) {
    return <Navigate to={`/invite/${pendingInvite}`} replace />;
  }

  const slug = workspace?.id ?? workspaces[0]?.id;
  if (slug) {
    // Preserve the full path + query params: /settings/sources?github_success=true → /w/slug/settings/sources?github_success=true
    return <Navigate to={`/w/${slug}${location.pathname}${location.search}`} replace />;
  }
  return <Navigate to="/new-workspace" replace />;
}

/** Redirect legacy chat detail: /chat/:id → /w/:slug/chat/:id */
function LegacyChatRedirect(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const { workspace, workspaces, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }
  const slug = workspace?.id ?? workspaces[0]?.id;
  if (slug && id) return <Navigate to={`/w/${slug}/chat/${id}`} replace />;
  if (slug) return <Navigate to={`/w/${slug}/chats`} replace />;
  return <Navigate to="/new-workspace" replace />;
}

/** Redirect legacy dashboard: /d/:slug → /w/:wsSlug/d/:slug */
function LegacyDashRedirect(): React.ReactElement {
  const { slug: dashSlug } = useParams<{ slug: string }>();
  const { workspace, workspaces, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }
  const wsSlug = workspace?.id ?? workspaces[0]?.id;
  if (wsSlug && dashSlug) return <Navigate to={`/w/${wsSlug}/d/${dashSlug}`} replace />;
  if (wsSlug) return <Navigate to={`/w/${wsSlug}/dashboards`} replace />;
  return <Navigate to="/new-workspace" replace />;
}

/** Redirect legacy workflow: /workflows/:id → /w/:slug/workflows/:id */
function LegacyWorkflowRedirect(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const { workspace, workspaces, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }
  const slug = workspace?.id ?? workspaces[0]?.id;
  if (slug && id) return <Navigate to={`/w/${slug}/workflows/${id}`} replace />;
  if (slug) return <Navigate to={`/w/${slug}/workflows`} replace />;
  return <Navigate to="/new-workspace" replace />;
}

export function AppRoutes(): React.ReactElement {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/auth/dev" element={<DevAuth />} />
      <Route path="/pub/:token" element={<PublicDashboard />} />
      <Route path="/invite/:inviteId" element={<AcceptInvite />} />
      <Route path="/new-workspace" element={<NewWorkspace />} />

      {/* Workspace routes: /w/:slug/... */}
      <Route
        path="/w/:slug"
        element={
          <ProtectedRoute>
            <WorkspaceRoute />
          </ProtectedRoute>
        }
      >
        <Route element={<AppLayout />}>
          <Route index element={<Chat />} />
          <Route path="chat/:id" element={<Chat />} />
          <Route path="chats" element={<ChatList />} />
          <Route path="d/:dashSlug" element={<Dashboard />} />
          <Route path="dashboards" element={<DashboardList />} />
          <Route path="workflows" element={<Workflows />} />
          <Route path="workflows/:id" element={<WorkflowDetail />} />
          <Route path="plans" element={<MeasurementPlanList />} />
          <Route path="plans/:id" element={<MeasurementPlanDetail />} />
          <Route path="docs" element={<Navigate to="../context" replace />} />
          <Route
            path="docs/:id"
            element={
              <Suspense fallback={<LoaderCircle className="mx-auto mt-12 size-6 animate-spin text-muted-foreground" />}>
                <DocDetail />
              </Suspense>
            }
          />
          <Route
            path="context"
            element={
              <Suspense fallback={<LoaderCircle className="mx-auto mt-12 size-6 animate-spin text-muted-foreground" />}>
                <ContextHub />
              </Suspense>
            }
          />
          <Route
            path="context/generated/:id"
            element={
              <Suspense fallback={<LoaderCircle className="mx-auto mt-12 size-6 animate-spin text-muted-foreground" />}>
                <DocDetail />
              </Suspense>
            }
          />
          <Route
            path="context/:kind/:id"
            element={
              <Suspense fallback={<LoaderCircle className="mx-auto mt-12 size-6 animate-spin text-muted-foreground" />}>
                <ContextItemDetail />
              </Suspense>
            }
          />
          <Route path="mcp" element={<McpClients />} />
          <Route path="settings" element={<SettingsShell />}>
            <Route index element={<Settings />} />
            <Route path="scm" element={<SettingsScm />} />
            <Route path="sources" element={<SettingsSources />} />
            <Route path="warehouse" element={<SettingsWarehouse />} />
            <Route path="integrations" element={<SettingsIntegrations />} />
            <Route path="slack" element={<SettingsSlack />} />
            <Route path="members" element={<SettingsMembers />} />
          </Route>
        </Route>
      </Route>

      {/* Legacy redirects — preserve resource params */}
      <Route path="/" element={<LegacyRedirect />} />
      <Route path="/chats" element={<LegacyRedirect />} />
      <Route path="/chat/:id" element={<LegacyChatRedirect />} />
      <Route path="/dashboards" element={<LegacyRedirect />} />
      <Route path="/d/:slug" element={<LegacyDashRedirect />} />
      <Route path="/workflows" element={<LegacyRedirect />} />
      <Route path="/workflows/:id" element={<LegacyWorkflowRedirect />} />
      <Route path="/settings/*" element={<LegacyRedirect />} />
    </Routes>
  );
}

export function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
